// Import 3rd-party dependencies
import { Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { map, shareReplay } from 'rxjs/operators';

// Import project-specific dependencies
import { environment } from '@app/environments/environment';
import { PeopleService } from '@app/services/people/people.service';

export interface IClickEventData {
  link?: string;
  name: string;
  title?: string;
  location?: string;
}

export interface ISearchInputData {
  term: string;
  type?: string;
}

export interface IProfileSetup {
  capabilities?: string[];
  interests?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class InsightsService {
  public appInsights: ApplicationInsights;
  userId: string;

  constructor(private peopleService: PeopleService) {
    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: environment.AZURE.APP_INSIGHTS.CONFIG_INSTRUMENTATION_KEY
      }
    });
    this.loadAppInsights();
  }

  loadAppInsights(): void {
    this.appInsights.loadAppInsights();
  }

  getUserId(): void {
    this.peopleService.currentUser$
      .pipe(
        map((user) => user?.id || ''),
        shareReplay()
      )
      .subscribe((res) => {
        this.userId = res;
      });
  }

  trackPageView(pageName: string): void {
    this.getUserId();
    this.appInsights.trackPageView({
      name: pageName,
      properties: {
        User: this.userId
      }
    });
  }

  trackClickEvent(event: IClickEventData): void {
    let name = '';
    if (event.name.split(' ').length > 8) {
      name = this.splitTitle(event.name);
    } else {
      name = event.name;
    }
    this.getUserId();
    this.appInsights.trackEvent(
      {
        name: `Click "${name}" from ${event.location}.`
      },
      {
        User: this.userId,
        Link: event.link || 'N/A',
        Title: event.title
      }
    );
  }

  trackProfile(profile: IProfileSetup): void {
    this.getUserId();
    this.appInsights.trackEvent(
      {
        name: 'Profile modification.'
      },
      {
        User: this.userId,
        Capabilities: profile.capabilities,
        Interests: profile.interests
      }
    );
  }

  trackSearchInput(search: ISearchInputData): void {
    this.getUserId();
    if (search.term) {
      if (search.type != null) {
        this.appInsights.trackEvent(
          {
            name: `Searched in: ${search.type}`
          },
          {
            User: this.userId
          }
        );
      }
      this.appInsights.trackEvent(
        {
          name: `Searched for: ${search.term}`
        },
        {
          searchTerm: search.term,
          User: this.userId,
          Type: search.type
        }
      );
    }
  }

  trackActivity(activity: string): void {
    let name = '';
    if (activity === 'skip') {
      name = 'Profile setup is skipped.';
    }
    this.getUserId();
    this.appInsights.trackEvent(
      {
        name: name
      },
      {
        User: this.userId
      }
    );
  }

  splitTitle(title: string): string {
    return title.split(/\s+/).slice(0, 7).join(' ').concat('...');
  }
}
